import { MessageSquarePlusIcon } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import { useMe, useSubmitFeedback } from '@/api/queries'
import type { FeedbackCategory } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatBytes } from '@/lib/format'
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_MAX_BODY_BYTES,
  FEEDBACK_MAX_TITLE_CHARS,
  feedbackProblem,
  redactionNotice,
  toRequest,
  utf8Bytes,
} from '@/pages/feedback'
import { cn } from '@/lib/utils'

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs disabled:opacity-50'

/**
 * "Send feedback" -- a report to the saltseed staff, from the user menu.
 *
 * The byte counter counts UTF-8 bytes because that is what the server caps. A secret-looking value
 * in the message is scrubbed server-side; the success screen says how many, so the sender knows
 * the report arrived altered.
 */
export function FeedbackDialog() {
  const me = useMe()
  const submit = useSubmitFeedback()
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState<FeedbackCategory>('bug')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [zoneId, setZoneId] = useState('')
  const zones = me.data?.zones ?? []

  const bytes = utf8Bytes(body)
  const draft = { category, title, body, zoneId }
  const problem = feedbackProblem(draft)
  const overCap = bytes > FEEDBACK_MAX_BODY_BYTES
  const sent = submit.data

  function reset() {
    submit.reset()
    setCategory('bug')
    setTitle('')
    setBody('')
    setZoneId('')
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (problem !== undefined || submit.isPending) return
    submit.mutate(toRequest(draft))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true)
        }}
        className="w-full justify-start text-muted-foreground hover:text-foreground"
      >
        <MessageSquarePlusIcon className="size-4" />
        Send feedback
      </Button>
      <DialogContent className="max-h-[90dvh] overflow-hidden">
        {sent === undefined ? (
          <form onSubmit={onSubmit} className="flex min-h-0 flex-col">
            <DialogHeader>
              <DialogTitle>Send feedback</DialogTitle>
              <DialogDescription>
                A report to the saltseed team. It is read by staff, not by your zone. Do not paste
                credentials; anything that looks like one is removed before it is stored.
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-5">
              <div className="space-y-1.5">
                <Label htmlFor="feedback-category">Category</Label>
                <select
                  id="feedback-category"
                  value={category}
                  onChange={(event) => {
                    setCategory(event.target.value as FeedbackCategory)
                  }}
                  className={SELECT_CLASS}
                >
                  {FEEDBACK_CATEGORIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-title">Title (optional)</Label>
                <Input
                  id="feedback-title"
                  value={title}
                  maxLength={FEEDBACK_MAX_TITLE_CHARS}
                  onChange={(event) => {
                    setTitle(event.target.value)
                  }}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-body">Message</Label>
                <Textarea
                  id="feedback-body"
                  value={body}
                  onChange={(event) => {
                    setBody(event.target.value)
                  }}
                  rows={8}
                  aria-invalid={overCap}
                  placeholder="What happened, what you expected, and how to reproduce it. Markdown is fine."
                />
                <p
                  data-testid="feedback-counter"
                  className={cn(
                    'text-right text-xs tabular',
                    overCap ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  {formatBytes(bytes)} of {formatBytes(FEEDBACK_MAX_BODY_BYTES)}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-zone">Zone (optional)</Label>
                <select
                  id="feedback-zone"
                  value={zoneId}
                  onChange={(event) => {
                    setZoneId(event.target.value)
                  }}
                  className={SELECT_CLASS}
                >
                  <option value="">No zone</option>
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </select>
              </div>

              {submit.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not send the feedback</AlertTitle>
                  <AlertDescription>{errorMessage(submit.error)}</AlertDescription>
                </Alert>
              ) : null}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false)
                  reset()
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={problem !== undefined || submit.isPending}>
                {submit.isPending ? 'Sending…' : 'Send'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="feedback-sent">
            <DialogHeader>
              <DialogTitle>Thank you</DialogTitle>
              <DialogDescription>Your feedback reached the saltseed team.</DialogDescription>
            </DialogHeader>
            {redactionNotice(sent.redactions) === undefined ? null : (
              <Alert>
                <AlertTitle>Some of your message was removed</AlertTitle>
                <AlertDescription>{redactionNotice(sent.redactions)}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                onClick={() => {
                  setOpen(false)
                  reset()
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
